import React, { useState, useEffect } from 'react';
import { X, Plus, Tag, Lightbulb, Sparkles } from 'lucide-react';

interface CaptureIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (idea: IdeaData) => void;
  initialData?: Idea | null;
}

interface Idea {
  _id?: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  priority: string;
  status: string;
}

interface IdeaData {
  title: string;
  description: string;
  category: string;
  tags: string[];
  priority: string;
  status: string;
}

const CaptureIdeaModal: React.FC<CaptureIdeaModalProps> = ({ isOpen, onClose, onAdd, initialData }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [priority, setPriority] = useState('Medium');
  const [status, setStatus] = useState('New');
  const [isGeneratingIdea, setIsGeneratingIdea] = useState(false);

  const predefinedCategories = [
    'App Idea',
    'Web App',
    'Social App',
    'EdTech',
    'Business Idea',
    'Product Concept',
    'Feature Request',
    'Research Topic',
    'Creative Project',
    'Other'
  ];

  const priorityOptions = ['High', 'Medium', 'Low'];
  const statusOptions = ['New', 'In Progress', 'Researching', 'On Hold', 'Completed'];

  // Populate form when editing
  useEffect(() => {
    if (isOpen && initialData) {
      setTitle(initialData.title);
      setDescription(initialData.description);
      setCategory(initialData.category);
      setTags(initialData.tags || []);
      setPriority(initialData.priority);
      setStatus(initialData.status);
    } else if (isOpen && !initialData) {
      // Clear form for new idea
      setTitle('');
      setDescription('');
      setCategory('');
      setTags([]);
      setPriority('Medium');
      setStatus('New');
    }
  }, [isOpen, initialData]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim() || !category.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const newIdea: IdeaData = {
      title: title.trim(),
      description: description.trim(),
      category: category.trim(),
      tags,
      priority,
      status
    };

    onAdd(newIdea);
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setTitle('');
    setDescription('');
    setCategory('');
    setTagInput('');
    setTags([]);
    setPriority('Medium');
    setStatus('New');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleAIGenerateIdea = async () => {
    if (!category) {
      alert('Please select a category first');
      return;
    }
    setIsGeneratingIdea(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/analyze/idea-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ category })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.idea) {
          setTitle(result.idea.title || '');
          setDescription(result.idea.description || '');
          setTags(result.idea.tags || []);
          setPriority(result.idea.priority || 'Medium');
          setStatus(result.idea.status || 'New');
        }
      } else {
        alert('Failed to generate idea. Please try again.');
      }
    } catch (error) {
      alert('Error generating idea. Please try again.');
    } finally {
      setIsGeneratingIdea(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Lightbulb className="text-yellow-500" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">
              {initialData ? 'Edit Idea' : 'Capture New Idea'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Category */}
          <div>
            <div className='flex flex-row justify-between items-center mb-4'>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <button
                  type="button"
                  onClick={handleAIGenerateIdea}
                  disabled={isGeneratingIdea || !category}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isGeneratingIdea ? (
                    <>
                      <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                    <Sparkles size={12} />        
                      AI Generate
                    </>
                  )}
                </button>
              </div>
            <div className="space-y-2">
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a category...</option>
                {predefinedCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <div className="text-xs text-gray-500">
                Or enter a custom category:
              </div>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Custom category..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your idea title..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your idea in detail..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Tags */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">Tags</div>              
            </div>
            <div className="space-y-2">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add tags..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center space-x-1 bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm"
                    >
                      <Tag size={12} />
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 text-gray-500 hover:text-red-500"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Priority and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Priority */}
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {priorityOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors btn-primary"
            >
              {initialData ? 'Update Idea' : 'Capture Idea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaptureIdeaModal;
